import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Search, Filter as FilterIcon } from 'lucide-react';
import { MODULE_TABS } from '../../shared/moduleTabs';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../shared/pagination';
import { useErrorToast, useDismissError } from '../../shared/ErrorToastContext';
import RowActionsMenu from '../../shared/RowActionsMenu';
import SavedFilterTabs from '../../shared/SavedFilterTabs';
import FilterDrawer from '../../shared/FilterDrawer';
import CancelButton from '../../shared/CancelButton';
import { CategoryPill } from '../../shared/Pill';
import '../../shared/ListRowHover.css';
import '../../shared/StyledInput.css';

// Same SavedFilterTabs pattern as Screen Help Settings — a quick-select
// dropdown plus the drawer for saved tabs, both bound to the same filters
// object.
const FILTER_SCREEN_KEY = 'field-help-settings';
const DEFAULT_FILTERS = { page_key: '' };
const FIELD_FILTER_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_FILTER_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85', fontWeight: 600 };

// Same 1-indexed Module numbering as Screen Help Settings (manualScreenController's
// MODULE_ORDER mirrors MODULE_TABS' own order) — a field's Module/Code is
// just whichever screen it lives on, so this reuses that mapping directly.
const MODULE_LABEL_BY_NUMBER = Object.fromEntries(MODULE_TABS.map((module, idx) => [idx + 1, module.label]));

function formatCode(row) {
  return row.module_number != null && row.sequence != null ? `${row.module_number}-${row.sequence}` : '—';
}

// Same "↺ restores the code-owned default" convention as ErrorCatalogPage's
// RevertButton — only rendered once the draft actually differs from the
// default. Resets the draft, not the server — nothing is saved until Save
// is clicked.
function RevertButton({ onClick, defaultValue, marginTop }) {
  return (
    <span
      onClick={onClick}
      title={defaultValue ? `Reset to default: "${defaultValue}"` : 'Clear'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        borderRadius: '50%',
        backgroundColor: '#2563eb',
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        cursor: 'pointer',
        flexShrink: 0,
        marginTop,
      }}
    >
      ↺
    </span>
  );
}

// Mirrors ScreenSettingsTable's one special case — every page_key routes at
// `/${page_key}` verbatim.
function screenPath(pageKey) {
  return `/${pageKey}`;
}

// The expanded row's editor — a local draft, only sent to the server on
// Save; Cancel (or navigating away, since the draft lives only in this
// component's state) discards it with nothing written. Replaces the old
// commit-on-blur-per-keystroke behaviour, which had no way to back out of
// an in-progress edit.
function FieldHelpEditor({ row, articleOptions, onSave, onCancel }) {
  const [bodyDraft, setBodyDraft] = useState(row.body_text_override ?? row.default_body_text);
  const [articleDraft, setArticleDraft] = useState(row.article_slug_override || '');
  const [saving, setSaving] = useState(false);

  const bodyChanged = bodyDraft !== (row.default_body_text || '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        body_text_override: bodyChanged ? bodyDraft : '',
        article_slug_override: articleDraft,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720 }}>
      <div>
        <div style={FIELD_LABEL_STYLE}>Help text</div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 4 }}>
          <textarea
            value={bodyDraft}
            rows={3}
            onChange={(e) => setBodyDraft(e.target.value)}
            style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', border: '1px solid #e3e7ee', borderRadius: 6, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit', color: '#1a1a1a', resize: 'vertical' }}
          />
          {bodyChanged && <RevertButton onClick={() => setBodyDraft(row.default_body_text || '')} defaultValue={row.default_body_text} marginTop={6} />}
        </div>
      </div>
      <div>
        <div style={FIELD_LABEL_STYLE}>Article</div>
        <select className="styled-input"
          value={articleDraft}
          onChange={(e) => setArticleDraft(e.target.value)}
          style={{ width: '100%', maxWidth: 360, padding: '6px 8px', borderRadius: 6, border: '1px solid #d0d5dd', fontSize: 14, marginTop: 4 }}
        >
          <option value="">
            {row.default_article_slug ? `— default (${row.default_article_slug}) —` : '— none —'}
          </option>
          {articleOptions.map((article) => (
            <option key={article.slug} value={article.slug}>{article.group} / {article.title}</option>
          ))}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: saving ? '#90caf9' : '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer' }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: '#ff6f61', color: '#fff', fontWeight: 600, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Admin-editable content for every FieldHelp "?" bubble in the app (layer 3
// of the in-app help stack; ScreenSettingsTable is layer 4's sibling
// screen). Unlike Screen Help, rows aren't synced from one central manifest
// — each FieldHelp registers its own default_* columns the first time it
// mounts (see FieldHelpRegistryContext), so a field that's never been
// rendered yet simply hasn't appeared here. Editing always writes
// body_text_override/article_slug_override, never default_body_text/
// default_article_slug — those stay code-owned, refreshed by sync, so an
// admin's wording is never silently reverted by a later code change.
//
// Rows stay collapsed to a single line by default (Screen/Field/preview),
// same as Screen Help, so a full page of entries is scannable at a glance.
// The kebab (⋮) — same convention as a Product row's — opens the row into a
// full-width editor (FieldHelpEditor) instead of squeezing a textarea into
// a table cell.
function FieldHelpTable() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [pagination, setPagination] = useState({ page: 1, limit, total: 0, pages: 1 });
  const [articleOptions, setArticleOptions] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [pageKeyOptions, setPageKeyOptions] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const { page_key: pageKeyFilter } = filters;
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  // Ad hoc "Filters" panel — draft is null while closed, so opening always
  // starts from the currently-applied filters rather than whatever was left
  // over from the last time it was open.
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };

  useDefaultPageSize(setLimit);

  useEffect(() => {
    axios.get('/api/manual-articles')
      .then((res) => setArticleOptions(res.data.data || []))
      .catch(() => setArticleOptions([]));
  }, []);

  // Distinct Screen list for the filter dropdown — fetched unpaginated and
  // separately from the main (filtered/paginated) row list, same split as
  // articleOptions above. { page_key, screen_label } so the dropdown can
  // show the same human-readable name as the Screen column, not the raw key.
  useEffect(() => {
    axios.get('/api/field-help', { params: { all: true } })
      .then((res) => {
        const byKey = new Map();
        (res.data.data || []).forEach((r) => {
          if (r.page_key && !byKey.has(r.page_key)) byKey.set(r.page_key, r.screen_label || r.page_key);
        });
        setPageKeyOptions(Array.from(byKey, ([page_key, screen_label]) => ({ page_key, screen_label })).sort((a, b) => a.screen_label.localeCompare(b.screen_label)));
      })
      .catch(() => setPageKeyOptions([]));
  }, []);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/field-help', { params: { search, page_key: pageKeyFilter, page, limit } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [search, pageKeyFilter, page, limit]);

  const saveEntry = async (row, updates) => {
    try {
      const res = await axios.patch(`/api/field-help/${row.id}`, updates);
      setRows((prev) => prev.map((r) => (r.id === row.id ? res.data.data : r)));
      setExpandedId(null);
      dismissError();
    } catch (err) {
      showError(err, 'Could not update this field help entry');
      throw err;
    }
  };

  // The drawer's field list for SavedFilterTabs — the same Screen field
  // that used to also live in an always-visible quick select above it,
  // duplicating this same field twice on screen.
  const renderDrawerFields = (draft, setDraftField) => (
    <div>
      <label style={FIELD_FILTER_LABEL_STYLE}>Screen</label><br />
      <select className="styled-input" value={draft.page_key} onChange={(e) => setDraftField('page_key', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
        <option value="">All screens</option>
        {pageKeyOptions.map((opt) => (
          <option key={opt.page_key} value={opt.page_key}>{opt.screen_label}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <h2 style={{ padding: '20px 24px 4px', margin: 0, fontWeight: 600, fontSize: 20 }}>Field Help Settings</h2>
      <p style={{ padding: '0 24px', margin: '0 0 12px', color: '#888', fontSize: 12 }}>
        Every "?" help bubble on a form, with its message and linked Manual article — both editable here, changes apply once you click Save. A field only appears here once its "?" has been opened somewhere in the app at least once. This screen can only edit a "?" that already exists — adding a new one to a field that doesn't have one yet still needs a developer.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '0 24px 14px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fff', border: '1px solid #d0d5dd', borderRadius: 8, maxWidth: 280 }}>
          <Search size={14} strokeWidth={1.75} color="#888" />
          <input
            type="text"
            placeholder="Search fields..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ border: 'none', outline: 'none', fontSize: 14, width: '100%' }}
          />
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div style={{ margin: '0 24px 14px' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the field help list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      <div style={{ margin: '0 24px', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '9%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '23%' }} />
            <col style={{ width: '27%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Code</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Module</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Screen</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Field</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Help Text</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No fields match.</td></tr>
            ) : rows.map((row) => {
              const bodyText = row.body_text_override ?? row.default_body_text;
              const isExpanded = expandedId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className="list-row"
                    onClick={() => setExpandedId(isExpanded ? null : row.id)}
                    style={{ cursor: 'pointer' }}
                    title="Click to edit"
                  >
                    <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatCode(row)}</td>
                    <td style={{ padding: '6px 8px' }}>
                      {MODULE_LABEL_BY_NUMBER[row.module_number] ? <CategoryPill value={MODULE_LABEL_BY_NUMBER[row.module_number]} /> : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: 13 }} onClick={(e) => e.stopPropagation()}>
                      {row.page_key ? (
                        <Link to={screenPath(row.page_key)} style={{ color: '#1976d2', fontSize: 13, textDecoration: 'none' }}>{row.screen_label || row.page_key}</Link>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: 14 }}>{row.label}</td>
                    <td style={{ padding: '6px 8px', fontSize: 13, color: '#5a6b85', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={bodyText}>
                      {bodyText}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu
                        ariaLabel="Field help actions"
                        items={[
                          { key: 'edit', label: isExpanded ? 'Collapse' : 'Edit', onClick: () => setExpandedId(isExpanded ? null : row.id) },
                        ]}
                      />
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={6} style={{ padding: '10px 16px 18px', background: '#f8fafc', borderBottom: '1px solid #e3e7ee' }}>
                        <FieldHelpEditor
                          row={row}
                          articleOptions={articleOptions}
                          onSave={(updates) => saveEntry(row, updates)}
                          onCancel={() => setExpandedId(null)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default FieldHelpTable;
